import { gql } from '@apollo/client';
import React, { memo, useEffect, useCallback, useReducer, useMemo } from 'react';
import { Clickable } from '@moda/om';
import { mapCursorToMax } from 'map-cursor-to-max';
import { ResponsiveImage } from '../ResponsiveImage';
import { ProductZoomedGallery, PRODUCT_ZOOMED_GALLERY_FRAGMENT } from '../ProductZoomedGallery';
import { DesktopProductMediaGalleryFragment } from '../../generated/types/DesktopProductMediaGalleryFragment';
import { getActiveVariantBy } from '../../lib/getActiveVariantBy';
import { DesktopProductMediaGalleryThumb } from './DesktopProductMediaGalleryThumb';
import './DesktopProductMediaGallery.scss';

export const DESKTOP_PRODUCT_MEDIA_GALLERY_VARIANT_FRAGMENT = gql`
  fragment DesktopProductMediaGalleryVariantFragment on VariantData {
    id
    primaryImage: primary_image_urls {
      medium
      large
    }
    alternateImages: alternate_image_urls {
      medium
      large
    }
  }
`;

export const DESKTOP_PRODUCT_MEDIA_GALLERY_FRAGMENT = gql`
  fragment DesktopProductMediaGalleryFragment on Variant {
    id
    name
    masterVariant: master_variants_data {
      ...DesktopProductMediaGalleryVariantFragment
    }
    otherVariants: other_variants_data {
      ...DesktopProductMediaGalleryVariantFragment
    }
    ...ProductZoomedGalleryFragment
  }
  ${DESKTOP_PRODUCT_MEDIA_GALLERY_VARIANT_FRAGMENT}
  ${PRODUCT_ZOOMED_GALLERY_FRAGMENT}
`;

export interface Props {
  variant: DesktopProductMediaGalleryFragment;
}

export enum Mode {
  Resting,
  Zoomed
}

interface State {
  cursor: number;
  mode: Mode;
}

export const PENDING_SRC = '//:0';

export type Action =
  | { type: 'SELECT'; payload: { cursor: number } }
  | { type: 'ZOOM' }
  | { type: 'CLOSE' };

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SELECT':
      return { ...state, cursor: action.payload.cursor };
    case 'ZOOM':
      return { ...state, mode: Mode.Zoomed };
    case 'CLOSE':
      return { ...state, mode: Mode.Resting };
  }
};

export const DesktopProductMediaGallery: React.FC<Props> = memo(({ variant }) => {
  const [state, dispatch] = useReducer(reducer, { cursor: 0, mode: Mode.Resting });

  const activeVariant = useMemo(() => getActiveVariantBy(variant, 'id', variant.id), [variant]);

  const images = [activeVariant.primaryImage, ...activeVariant.alternateImages];

  const handleClick = useCallback(
    (cursor: number) => dispatch({ type: 'SELECT', payload: { cursor } }),
    []
  );

  const handleZoom = useCallback(() => dispatch({ type: 'ZOOM' }), []);
  const handleClose = useCallback(() => dispatch({ type: 'CLOSE' }), []);

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
          dispatch({ type: 'SELECT', payload: { cursor: state.cursor + 1 } });
          break;
        case 'ArrowLeft':
          dispatch({ type: 'SELECT', payload: { cursor: state.cursor - 1 } });
          break;
        default:
          break;
      }
    },
    [state.cursor]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);
    return (): void => window.removeEventListener('keydown', handleKeydown);
  }, [handleKeydown]);

  const activeIndex = useMemo(() => mapCursorToMax(state.cursor, images.length), [
    images.length,
    state.cursor
  ]);

  return (
    <>
      <div className="DesktopProductMediaGallery">
        <div className="DesktopProductMediaGallery__selector">
          {images.map((srcs, index) => (
            <DesktopProductMediaGalleryThumb
              key={index}
              src={srcs?.medium || PENDING_SRC}
              alt={variant.name || 'Product view'}
              index={index}
              onClick={handleClick}
              selected={activeIndex === index}
            />
          ))}
        </div>

        <div className="DesktopProductMediaGallery__viewer">
          <Clickable className="DesktopProductMediaGallery__focus" onClick={handleZoom}>
            <ResponsiveImage
              className="DesktopProductMediaGallery__image"
              src={images[activeIndex]?.large || PENDING_SRC}
              alt={variant.name || 'Product thumbnail'}
              maxWidth={700}
              maxHeight={600}
            />
          </Clickable>
        </div>
      </div>

      {state.mode === Mode.Zoomed && (
        <ProductZoomedGallery
          onClose={handleClose}
          activeVariant={activeVariant}
          activeIndex={activeIndex}
        />
      )}
    </>
  );
});
